import { createFileRoute, Link } from "@tanstack/react-router";
import { absoluteUrl } from "@/lib/site";
import { GATES } from "@/lib/gates";
import { breadcrumbs, itemList, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";

export const Route = createFileRoute("/gates/")({
  head: () => ({
    ...seoMeta({
      title: "The 47 Gates of Consciousness — KAfrequency",
      description:
        "Forty-seven doorways into consciousness — the philosophical backbone of the KAfrequency ecosystem. Each Gate connects to music, wisdom, activations, and books that resonate with its frequency.",
      path: "/gates",
    }),
    scripts: [
      ldScript(
        pageNode({
          type: "CollectionPage",
          id: `${absoluteUrl("/gates")}#gates`,
          url: absoluteUrl("/gates"),
          name: "The 47 Gates of Consciousness",
          description:
            "A living index of forty-seven doorways into awareness, discipline, and creativity, authored by Buddha KA.",
          about: [personRef(), orgRef()],
          mainEntity: itemList(
            GATES.map((g) => ({ path: `/gates/${g.n}`, name: `Gate ${g.n} — ${g.title}` })),
          ),
        }),
      ),
      ldScript(breadcrumbs([["The 47 Gates", "/gates"]])),
    ],
  }),
  component: GatesIndex,
});

function GatesIndex() {
  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
      <header className="mx-auto max-w-3xl text-center">
        <p className="text-[10px] uppercase tracking-[0.4em] text-gold">The 47 Gates</p>
        <h1 className="mt-4 font-serif text-4xl leading-tight sm:text-6xl">
          Forty-seven doorways.<br />
          <span className="text-gold-luminous">One consciousness.</span>
        </h1>
        <p className="mt-6 text-base text-muted-foreground sm:text-lg">
          Each Gate is a station on the path of awareness. Walk them in sequence,
          or follow the one calling loudest today — every Gate carries music,
          wisdom, activations, and books that resonate with its frequency.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link
            to="/gates/map"
            className="rounded-md border border-gold px-4 py-2 text-sm text-gold-luminous hover:bg-gold/10"
          >
            View the Life Journey Map →
          </Link>
          <Link
            to="/codex"
            className="rounded-md border border-gold/60 px-4 py-2 text-sm text-gold-luminous hover:bg-gold/10"
          >
            Enter the Codex
          </Link>
          <Link
            to="/gates/$n"
            params={{ n: "1" }}
            className="rounded-md bg-gold px-4 py-2 text-sm text-obsidian hover:bg-gold-luminous"
          >
            Begin at Gate 1
          </Link>
        </div>
      </header>


      <ol className="mt-16 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {GATES.map((g) => (
          <li key={g.n}>
            <Link
              to="/gates/$n"
              params={{ n: String(g.n) }}
              className="group flex h-full flex-col gap-2 rounded-xl border hairline bg-obsidian/60 p-5 backdrop-blur transition-all hover:border-gold hover:bg-obsidian/80"
            >
              <div className="flex items-baseline justify-between">
                <span className="text-[10px] uppercase tracking-[0.3em] text-gold">
                  Gate {String(g.n).padStart(2, "0")}
                </span>
                <span aria-hidden className="font-serif text-lg text-gold/60 group-hover:text-gold-luminous">
                  ❖
                </span>
              </div>
              <h2 className="font-serif text-lg text-ivory group-hover:text-gold-luminous">
                {g.title}
              </h2>
              <p className="line-clamp-3 text-xs text-muted-foreground">
                {g.reflection}
              </p>
            </Link>
          </li>
        ))}
      </ol>
    </div>
  );
}
